'use client';

import { RevealOnScroll } from '@/shared/ui/reveal-on-scroll';
import { useTranslations } from 'next-intl';
import styles from './SecFeatureIntro.module.scss';

type FeatureCardProps = {
  title: string;
  description: string;
  iconSrc: string;
  iconWidth: number;
  iconHeight: number;
  iconRight: number;
  iconBottom: number;
};

function FeatureCard({
  title,
  description,
  iconSrc,
  iconWidth,
  iconHeight,
  iconRight,
  iconBottom,
}: FeatureCardProps) {
  return (
    <div className={styles.featureCard}>
      <div aria-hidden="true" className={styles.glowWrap}>
        <img
          alt=""
          className={styles.glow}
          src="/intro/glow.svg"
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className={styles.cardShell}>
        <div aria-hidden="true" className={styles.cardOverlay} />
        <div aria-hidden="true" className={styles.cardBorder} />

        <p className={styles.cardTitle}>{title}</p>

        <div className={styles.cardDescription}>{description}</div>
      </div>

      <div
        className={styles.iconWrap}
        style={{ right: iconRight, bottom: iconBottom }}
      >
        <img
          alt=""
          className={styles.icon}
          height={iconHeight}
          src={iconSrc}
          width={iconWidth}
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  );
}

// 라벨은 intro 네임스페이스(featureIntro.cards.{key})에서 해석한다.
const features = [
  {
    key: 'detail',
    iconSrc: '/intro/ic_pdp.svg',
    iconWidth: 121,
    iconHeight: 123,
    iconRight: 16.6,
    iconBottom: 37.58,
  },
  {
    key: 'banner',
    iconSrc: '/intro/ic_banner.svg',
    iconWidth: 120,
    iconHeight: 190,
    iconRight: 3.35,
    iconBottom: 5.18,
  },
  {
    key: 'video',
    iconSrc: '/intro/ic_video.svg',
    iconWidth: 117,
    iconHeight: 130,
    iconRight: 19.71,
    iconBottom: 25.98,
  },
  {
    key: 'aistudio',
    iconSrc: '/intro/ic_aistudio.svg',
    iconWidth: 129,
    iconHeight: 129,
    iconRight: 8.06,
    iconBottom: 14.35,
  },
] as const;

export function SecFeatureIntro() {
  const t = useTranslations('intro');

  return (
    <section
      id="feature-intro"
      data-section="feature-intro"
      className={styles.section}
    >
      <div className={styles.container}>
        <RevealOnScroll delayMs={100} y={20}>
          <div className={styles.headingBlock}>
            <p className={styles.heading}>{t('featureIntro.heading')}</p>
          </div>
        </RevealOnScroll>

        <RevealOnScroll delayMs={250} y={20}>
          <div className={styles.subheadingBlock}>
            <p className={styles.subheading}>{t('featureIntro.subheading')}</p>
          </div>
        </RevealOnScroll>

        <div className={styles.grid}>
          {features.map((feature) => (
            <FeatureCard
              key={feature.key}
              description={t(`featureIntro.cards.${feature.key}.description`)}
              iconBottom={feature.iconBottom}
              iconHeight={feature.iconHeight}
              iconRight={feature.iconRight}
              iconSrc={feature.iconSrc}
              iconWidth={feature.iconWidth}
              title={t(`featureIntro.cards.${feature.key}.title`)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
